/**
 * Bitcoin signing utility for Bitcoin wallets (Unisat, Xverse, OKX, OneKey)
 * Provides message signing functionality similar to EIP-712 for Ethereum
 * 
 * Uses Bitcoin wallet provider APIs to sign messages with ECDSA signatures
 * Supports multiple signing methods: Legacy, BIP-137, and BIP-322
 */

/**
 * Bitcoin message payload structure
 * Similar to EIP-712 message structure but for Bitcoin
 */
export interface BitcoinMessagePayload {
  action: string;
  owner: string;
  agent: string;
  expiresInDays: number;
  permissions: string[];
  nonce: string;
  agentName: string;
  timestamp?: number;
  deadline?: string;
}

/**
 * Bitcoin signing method types
 */
export type BitcoinSigningMethod = 'legacy' | 'bip137' | 'bip322';

/**
 * Bitcoin signature format types
 */
export type BitcoinSignatureFormat = 'der' | 'compact' | 'bip322' | 'base64';

/**
 * Bitcoin signing result
 */
export interface BitcoinSignResult {
  signature: string; // Hex or base64 encoded
  address: string;
  method: BitcoinSigningMethod;
  format: BitcoinSignatureFormat;
}

/**
 * Unisat Wallet Provider interface
 */
export interface UnisatProvider {
  requestAccounts(): Promise<string[]>;
  signMessage(
    message: string,
    type?: 'ecdsa' | 'bip322-simple'
  ): Promise<string>;
  signPsbt(psbt: string, options?: any): Promise<string>;
  getAccounts(): Promise<string[]>;
  getPublicKey(): Promise<string>;
  switchNetwork(network: 'livenet' | 'testnet'): Promise<void>;
  on(event: string, handler: (args: any) => void): void;
  removeListener(event: string, handler: (args: any) => void): void;
}

/**
 * Xverse Wallet Provider interface
 */
export interface XverseProvider {
  request(method: string, params?: any): Promise<any>;
  getAccounts(): Promise<string[]>;
  signMessage(message: string | Uint8Array): Promise<{
    address: string;
    signature: string; // Base64 encoded
  }>;
  signPsbt(psbt: string): Promise<string>;
}

/**
 * OKX Bitcoin Wallet Provider interface
 */
export interface OKXBitcoinProvider {
  connect(): Promise<string[]>;
  signMessage(signStr: string, type?: 'ecdsa' | 'bip322-simple'): Promise<string>;
  signPsbt(psbt: string): Promise<string>;
}

/**
 * Bitcoin wallet information
 */
export interface BitcoinWalletInfo {
  name: string;
  provider: any;
  isAvailable: boolean;
  supportsBIP322: boolean;
  addressFormat: 'legacy' | 'segwit' | 'taproot' | 'all';
}

/**
 * Detect available Bitcoin wallets
 * 
 * IMPORTANT: OneKey wallet provides window.unisat for backward compatibility.
 * To distinguish between actual Unisat wallet and OneKey's backward-compatible provider,
 * we check for window.$onekey.btc first. If that exists, we treat window.unisat as OneKey.
 */
export function detectBitcoinWallets(): BitcoinWalletInfo[] {
  if (typeof window === "undefined") return [];

  const wallets: BitcoinWalletInfo[] = [];
  const hasOneKeyWallet = !!(window as any).$onekey;
  // Check for Bitget early to avoid confusion with Unisat
  const hasBitgetWallet = !!(window as any).bitkeep?.isBitKeep || !!(window as any).bitkeep?.ethereum?.isBitEthereum;

  // Check OneKey Bitcoin (must be checked before Unisat to avoid confusion)
  // OneKey provides window.$onekey.btc (native) OR window.unisat (backward compatibility)
  if ((window as any).$onekey?.btc) {
    // Native OneKey Bitcoin provider
    wallets.push({
      name: 'OneKey',
      provider: (window as any).$onekey.btc,
      isAvailable: true,
      supportsBIP322: true, // OneKey supports BIP-322 via Unisat-compatible API
      addressFormat: 'all'
    });
  } else if (hasOneKeyWallet && (window as any).unisat) {
    // OneKey provides window.unisat for backward compatibility
    wallets.push({
      name: 'OneKey',
      provider: (window as any).unisat,
      isAvailable: true,
      supportsBIP322: true, // OneKey supports BIP-322 via Unisat-compatible API
      addressFormat: 'all'
    });
  }

  // Check Unisat (only if not already detected as OneKey or Bitget)
  // IMPORTANT: Both OneKey and Bitget may provide window.unisat for backward compatibility
  if ((window as any).unisat && !hasOneKeyWallet && !hasBitgetWallet) {
    wallets.push({
      name: 'Unisat',
      provider: (window as any).unisat,
      isAvailable: true,
      supportsBIP322: true, // Unisat supports BIP-322
      addressFormat: 'all'
    });
  }

  // Check Xverse
  if ((window as any).BitcoinProvider || (window as any).XverseProvider) {
    wallets.push({
      name: 'Xverse',
      provider: (window as any).BitcoinProvider || (window as any).XverseProvider,
      isAvailable: true,
      supportsBIP322: true, // Xverse supports BIP-322
      addressFormat: 'all'
    });
  }

  // Check OKX
  if ((window as any).okxwallet?.bitcoin) {
    wallets.push({
      name: 'OKX',
      provider: (window as any).okxwallet.bitcoin,
      isAvailable: true,
      supportsBIP322: false, // Check OKX docs for BIP-322 support
      addressFormat: 'all'
    });
  }

  // Check Bitget (must be checked before standalone Unisat to avoid confusion)
  // Bitget uses window.bitkeep.unisat for Bitcoin support (primary)
  // Bitget may also shim window.unisat for compatibility (only if Bitget is detected via other means)
  // IMPORTANT: Check for window.bitkeep first to distinguish from standalone Unisat
  // 
  // Per analysis of bitget-inpage.txt:
  // - Primary: window.bitkeep.unisat (Bitcoin provider)
  // - Optional shim: window.unisat (only if Bitget is detected via window.bitkeep flags)
  // - Detection: Check window.bitkeep.isBitKeep or window.bitkeep.ethereum.isBitEthereum
  // - Connection: window.bitkeep.unisat.requestAccounts() or window.bitkeep.unisat.setIdenties()
  // - Connected State: window.bitkeep.unisat.selectedAddress (post-connection address)
  // Note: hasBitgetWallet is already defined above to avoid confusion with Unisat detection
  
  if (hasBitgetWallet) {
    // Primary: Check window.bitkeep.unisat (Bitget's native Bitcoin provider)
    if ((window as any).bitkeep?.unisat) {
      wallets.push({
        name: 'Bitget',
        provider: (window as any).bitkeep.unisat,
        isAvailable: true,
        supportsBIP322: true, // Bitget uses Unisat-compatible API which supports BIP-322
        addressFormat: 'all'
      });
    }
    // Secondary: Check window.unisat shim (only if Bitget is already detected)
    // Per analysis: Bitget may shim window.unisat for compatibility, but only if Bitget is detected
    // We check this AFTER confirming Bitget via window.bitkeep flags to avoid confusion
    else if ((window as any).unisat && !hasOneKeyWallet) {
      // Only use window.unisat as Bitget if:
      // 1. Bitget is detected via window.bitkeep flags (hasBitgetWallet === true)
      // 2. OneKey is not detected (OneKey also provides window.unisat for backward compatibility)
      // 3. window.bitkeep.unisat is not available (fallback to shim)
      wallets.push({
        name: 'Bitget',
        provider: (window as any).unisat,
        isAvailable: true,
        supportsBIP322: true, // Bitget uses Unisat-compatible API which supports BIP-322
        addressFormat: 'all'
      });
    }
  }

  return wallets;
}

/**
 * Get preferred Bitcoin wallet (OneKey > Unisat > Xverse > OKX > Bitget)
 */
export function getPreferredBitcoinWallet(): BitcoinWalletInfo | null {
  const wallets = detectBitcoinWallets();
  // Prefer OneKey > Unisat > Xverse > OKX > Bitget
  return wallets.find(w => w.name === 'OneKey') ||
         wallets.find(w => w.name === 'Unisat') ||
         wallets.find(w => w.name === 'Xverse') ||
         wallets.find(w => w.name === 'OKX') ||
         wallets.find(w => w.name === 'Bitget') ||
         wallets[0] ||
         null;
}

/**
 * Check if any Bitcoin wallet is available (with actual Bitcoin provider)
 */
export function isBitcoinWalletAvailable(): boolean {
  return detectBitcoinWallets().length > 0;
}

/**
 * Check if OKX wallet is installed (even if Bitcoin provider not immediately available)
 * This helps show Bitcoin option when OKX is installed but user needs to switch networks
 */
export function isOKXWalletInstalled(): boolean {
  if (typeof window === "undefined") return false;
  return !!(window as any).okxwallet;
}

/**
 * Convert base64 to hex string
 */
function base64ToHex(base64: string): string {
  try {
    // For browser environment
    if (typeof Buffer === 'undefined') {
      const binaryString = atob(base64);
      return Array.from(binaryString)
        .map(char => char.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('');
    }
    // For Node.js environment
    return Buffer.from(base64, 'base64').toString('hex');
  } catch (error) {
    throw new Error(`Failed to convert base64 to hex: ${error}`);
  }
}

/**
 * Normalize Bitcoin signature to hex format
 */
function normalizeBitcoinSignature(
  signature: string,
  format: BitcoinSignatureFormat
): string {
  if (format === 'base64') {
    // Convert base64 to hex
    return base64ToHex(signature);
  }
  
  if (format === 'bip322') {
    // BIP-322 signatures are transaction-based
    // Return as-is for now (may need special handling)
    return signature;
  }
  
  // Already hex format, remove 0x prefix if present
  return signature.startsWith('0x') ? signature.slice(2) : signature;
}

/**
 * Sign message with Unisat wallet (or Unisat-compatible wallet like Bitget)
 * @param message - Message to sign
 * @param method - Signing method (default: 'bip137')
 * @param provider - Optional provider to use (defaults to window.unisat)
 */
export async function signMessageWithUnisat(
  message: string,
  method: BitcoinSigningMethod = 'bip137',
  provider?: UnisatProvider
): Promise<BitcoinSignResult> {
  // Use provided provider or fall back to window.unisat
  const unisatProvider = provider || ((window as any).unisat as UnisatProvider);
  
  if (!unisatProvider) {
    throw new Error("Unisat wallet not found");
  }

  // Request accounts
  const accounts = await unisatProvider.requestAccounts();
  if (accounts.length === 0) {
    throw new Error("No accounts found in Unisat wallet");
  }

  const address = accounts[0];

  // Sign message
  let signature: string;
  if (method === 'bip322') {
    signature = await unisatProvider.signMessage(message, 'bip322-simple');
  } else {
    signature = await unisatProvider.signMessage(message, 'ecdsa');
  }

  return {
    signature: normalizeBitcoinSignature(signature, method === 'bip322' ? 'bip322' : 'compact'),
    address,
    method,
    format: method === 'bip322' ? 'bip322' : 'compact'
  };
}

/**
 * Sign message with Xverse wallet
 */
export async function signMessageWithXverse(
  message: string,
  address: string
): Promise<BitcoinSignResult> {
  const provider = ((window as any).BitcoinProvider || (window as any).XverseProvider) as XverseProvider;
  
  if (!provider) {
    throw new Error("Xverse wallet not found");
  }

  // Sign message
  const result = await provider.request('signMessage', {
    address,
    message
  });

  // Xverse returns base64 signature, convert to hex
  const signatureHex = normalizeBitcoinSignature(result.signature, 'base64');

  return {
    signature: signatureHex,
    address: result.address,
    method: 'bip137', // Xverse uses BIP-137 format
    format: 'compact'
  };
}

/**
 * Sign message with OKX wallet
 */
export async function signMessageWithOKX(
  message: string,
  address: string
): Promise<BitcoinSignResult> {
  const provider = (window as any).okxwallet?.bitcoin as OKXBitcoinProvider;
  
  if (!provider) {
    throw new Error("OKX Bitcoin wallet not found");
  }

  // Connect if needed
  const accounts = await provider.connect();
  if (!accounts.includes(address)) {
    throw new Error("Address not found in OKX wallet");
  }

  // Sign message
  // OKX signMessage API: signMessage(signStr, type?)
  // The address is not needed as a parameter - the wallet uses the connected address
  // Type defaults to 'ecdsa' if not specified
  const signature = await provider.signMessage(message, 'ecdsa');

  return {
    signature: normalizeBitcoinSignature(signature, 'compact'),
    address,
    method: 'bip137',
    format: 'compact'
  };
}

/**
 * Sign message with OneKey wallet
 * OneKey uses Unisat-compatible API (either via window.$onekey.btc or window.unisat)
 */
export async function signMessageWithOneKey(
  message: string,
  method: BitcoinSigningMethod = 'bip137'
): Promise<BitcoinSignResult> {
  // Get OneKey Bitcoin provider (prefers $onekey.btc, falls back to unisat if OneKey detected)
  let provider: any = null;
  
  if ((window as any).$onekey?.btc) {
    provider = (window as any).$onekey.btc;
  } else if ((window as any).$onekey && (window as any).unisat) {
    provider = (window as any).unisat;
  }
  
  if (!provider) {
    throw new Error("OneKey Bitcoin wallet not found");
  }

  // Request accounts
  const accounts = await provider.requestAccounts();
  if (accounts.length === 0) {
    throw new Error("No accounts found in OneKey Bitcoin wallet");
  }

  const address = accounts[0];

  // Sign message (OneKey uses Unisat-compatible API)
  let signature: string;
  if (method === 'bip322') {
    signature = await provider.signMessage(message, 'bip322-simple');
  } else {
    signature = await provider.signMessage(message, 'ecdsa');
  }

  return {
    signature: normalizeBitcoinSignature(signature, method === 'bip322' ? 'bip322' : 'compact'),
    address,
    method,
    format: method === 'bip322' ? 'bip322' : 'compact'
  };
}

/**
 * Universal Bitcoin message signing (auto-detects wallet)
 * 
 * @param payload - Message payload to sign
 * @param address - Bitcoin address to sign with (optional, will use wallet's first address if not provided)
 * @param preferredMethod - Preferred signing method (default: 'bip137')
 * @returns BitcoinSignResult with signature and metadata
 */
export async function signBitcoinMessage(
  payload: BitcoinMessagePayload,
  address?: string,
  preferredMethod: BitcoinSigningMethod = 'bip137'
): Promise<BitcoinSignResult> {
  const message = JSON.stringify(payload);

  // Detect available wallet
  const wallet = getPreferredBitcoinWallet();
  if (!wallet) {
    throw new Error("No Bitcoin wallet found. Please install OneKey, Unisat, Xverse, or OKX wallet.");
  }

  // Use BIP-322 if wallet supports it and preferred method is bip322
  const signingMethod = (preferredMethod === 'bip322' && wallet.supportsBIP322) 
    ? 'bip322' 
    : 'bip137';

  try {
    if (wallet.name === 'OneKey') {
      return await signMessageWithOneKey(message, signingMethod);
    } else if (wallet.name === 'Unisat') {
      return await signMessageWithUnisat(message, signingMethod, wallet.provider);
    } else if (wallet.name === 'Bitget') {
      // Bitget uses Unisat-compatible API, so we can use the same signing function
      return await signMessageWithUnisat(message, signingMethod, wallet.provider);
    } else if (wallet.name === 'Xverse') {
      // Xverse needs address
      if (!address) {
        const accounts = await wallet.provider.getAccounts();
        if (accounts.length === 0) {
          throw new Error("No accounts found in Xverse wallet");
        }
        address = accounts[0];
      }
      if (!address) {
        throw new Error("Address is required for Xverse signing");
      }
      return await signMessageWithXverse(message, address);
    } else if (wallet.name === 'OKX') {
      // OKX needs address
      if (!address) {
        const accounts = await wallet.provider.connect();
        if (accounts.length === 0) {
          throw new Error("No accounts found in OKX wallet");
        }
        address = accounts[0];
      }
      if (!address) {
        throw new Error("Address is required for OKX signing");
      }
      return await signMessageWithOKX(message, address);
    } else {
      throw new Error(`Unsupported wallet: ${wallet.name}`);
    }
  } catch (error) {
    throw new Error(
      `Failed to sign with ${wallet.name}: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Get Bitcoin address from wallet
 * Returns the first address from the preferred wallet
 */
export async function getBitcoinAddress(): Promise<string> {
  const wallet = getPreferredBitcoinWallet();
  if (!wallet) {
    throw new Error("No Bitcoin wallet found");
  }

  try {
    if (wallet.name === 'OneKey') {
      // OneKey Bitcoin uses Unisat-compatible API
      const accounts = await wallet.provider.requestAccounts();
      if (accounts.length === 0) {
        throw new Error("No accounts found in OneKey Bitcoin wallet");
      }
      return accounts[0];
    } else if (wallet.name === 'Unisat') {
      const accounts = await wallet.provider.requestAccounts();
      if (accounts.length === 0) {
        throw new Error("No accounts found in Unisat wallet");
      }
      return accounts[0];
    } else if (wallet.name === 'Bitget') {
      // Bitget uses Unisat-compatible API
      const accounts = await wallet.provider.requestAccounts();
      if (accounts.length === 0) {
        throw new Error("No accounts found in Bitget wallet");
      }
      return accounts[0];
    } else if (wallet.name === 'Xverse') {
      const accounts = await wallet.provider.getAccounts();
      if (accounts.length === 0) {
        throw new Error("No accounts found in Xverse wallet");
      }
      return accounts[0];
    } else if (wallet.name === 'OKX') {
      // OKX Bitcoin uses requestAccounts() or connect() with different return formats
      let address: string | undefined;
      
      // Try requestAccounts() first (preferred for v2.77.1+)
      if (typeof wallet.provider.requestAccounts === 'function') {
        const accounts = await wallet.provider.requestAccounts();
        if (accounts && accounts.length > 0) {
          address = accounts[0];
        }
      }
      
      // Fallback to connect()
      if (!address && typeof wallet.provider.connect === 'function') {
        const result = await wallet.provider.connect();
        
        // connect() can return either array or object
        if (Array.isArray(result)) {
          if (result.length > 0) {
            address = result[0];
          }
        } else if (result && typeof result === 'object') {
          if (result.address) {
            address = result.address;
          } else if (Array.isArray(result.accounts) && result.accounts.length > 0) {
            address = result.accounts[0];
          }
        }
      }
      
      if (!address) {
        throw new Error("No accounts found in OKX wallet");
      }
      return address;
    } else {
      throw new Error(`Unsupported wallet: ${wallet.name}`);
    }
  } catch (error) {
    throw new Error(
      `Failed to get address from ${wallet.name}: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Verify that a string is a valid Bitcoin address
 * Supports Legacy (1...), SegWit (bc1q...), Taproot (bc1p...), and Nested SegWit (3...)
 */
export function isValidBitcoinAddress(address: string): boolean {
  if (!address || address.length < 26 || address.length > 62) {
    return false;
  }

  // Legacy addresses (P2PKH): start with 1 (mainnet) or m/n (testnet)
  if (/^[13mn][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(address)) {
    return true;
  }

  // SegWit addresses (P2WPKH): start with bc1q (mainnet) or tb1q (testnet)
  if (/^(bc1q|tb1q)[a-z0-9]{38,58}$/.test(address)) {
    return true;
  }

  // Taproot addresses (P2TR): start with bc1p (mainnet) or tb1p (testnet)
  if (/^(bc1p|tb1p)[a-z0-9]{58}$/.test(address)) {
    return true;
  }

  // Nested SegWit (P2SH): start with 3 (mainnet) or 2 (testnet)
  if (/^[23][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(address)) {
    return true;
  }

  return false;
}

// Type declarations for global window object
// Note: okxwallet and $onekey are fully declared in useAgentDelegation.ts
// This declaration only adds Bitcoin-specific properties that aren't in useAgentDelegation.ts
declare global {
  interface Window {
    unisat?: UnisatProvider;
    BitcoinProvider?: XverseProvider;
    XverseProvider?: XverseProvider;
    // okxwallet and $onekey are declared in useAgentDelegation.ts to avoid type conflicts
    // We only reference them here, not redeclare them
  }
}
